import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, IconButton
} from '@mui/material';
import { Delete, Edit, Save, Add } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import { downloadTextAsPdfFromBackend } from '../utils/pdf';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { loadCanvasQrCode } from '../utils/envelope';
import { generateToken } from '../utils/token';
import { buildApiUrl } from '../config/api';

function AdminParticipants() {
    const { token } = useAuth();
    const [participants, setParticipants] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [newNickname, setNewNickname] = useState('');
    const [editId, setEditId] = useState(null);
    const [editNickname, setEditNickname] = useState('');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [participantToDelete, setParticipantToDelete] = useState(null);
    const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
    const [participantDetails, setParticipantDetails] = useState(null);
    const [envelopes, setEnvelopes] = useState([]);

    useEffect(() => {
        if (token) fetchParticipants();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Adiciona campo has_qrcode ao carregar participantes
    const fetchParticipants = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await fetch(buildApiUrl('admin/participants'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar participantes');
            const data = await response.json();
            setParticipants(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = async () => {
        setError(''); setSuccess('');
        if (!newNickname.trim()) {
            setError('SUBJID é obrigatório.');
            return;
        }
        if (participants.some(p => p.nickname === newNickname.trim())) {
            setError('SUBJID já existe.');
            return;
        }
        try {
            const response = await fetch(buildApiUrl('admin/participants'), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ nickname: newNickname.trim() })
            });
            if (!response.ok) throw new Error('Erro ao adicionar participante');
            setNewNickname('');
            setSuccess('Participante adicionado!');
            fetchParticipants();
        } catch (err) { setError(err.message); }
    };

    const handleDelete = async (id) => {
        setError(''); setSuccess('');
        setParticipantToDelete(participants.find(p => p.id === id));
        setOpenDeleteDialog(true);
    };
    const confirmDelete = async () => {
        if (!participantToDelete) return;
        try {
            const response = await fetch(buildApiUrl(`admin/participants/${participantToDelete.id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao remover participante');
            setSuccess('Participante removido!');
            fetchParticipants();
        } catch (err) { setError(err.message); }
        setOpenDeleteDialog(false);
        setParticipantToDelete(null);
    };

    const handleEdit = (id, nickname) => {
        setEditId(id);
        setEditNickname(nickname);
    };

    const handleSave = async (id) => {
        setError(''); setSuccess('');
        if (!editNickname.trim()) {
            setError('SUBJID é obrigatório.');
            return;
        }
        if (participants.some(p => p.nickname === editNickname.trim() && p.id !== id)) {
            setError('SUBJID já existe.');
            return;
        }
        try {
            const response = await fetch(buildApiUrl(`admin/participants/${id}`), {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ nickname: editNickname.trim() })
            });
            if (!response.ok) throw new Error('Erro ao atualizar participante');
            setEditId(null);
            setEditNickname('');
            setSuccess('Participante atualizado!');
            fetchParticipants();
        } catch (err) { setError(err.message); }
    };

    // Função para abrir modal de detalhes
    const handleViewDetails = async (participant) => {
        setParticipantDetails(participant);
        try {
            const resp = await fetch(buildApiUrl(`admin/participant/${participant.id}/envelopes`), { headers: { 'Authorization': `Bearer ${token}` } });
            if (resp.ok) {
                const data = await resp.json();
                setEnvelopes(data);
                setError('');
            } else {
                setEnvelopes([]);
                setError('Erro ao buscar envelopes do participante.');
            }
        } catch {
            setEnvelopes([]);
            setError('Erro ao buscar envelopes do participante.');
        }
        setOpenDetailsDialog(true);
    };
    // Função para imprimir sentença
    const handlePrintSentence = (envelope) => {
        const uuid = envelope.qr_uuid || 'N/A';
        downloadTextAsPdfFromBackend(uuid);
    };

    const handleDownloadQr = async (envelope) => {
        // 3. Gera o token normalmente
        const expToken = generateToken(envelope.participant_id, envelope.envelope_id);
        const baseUrl = window.location.origin;
        const basename = process.env.PUBLIC_URL || '';
        // 4. O link agora usa apenas o UUID (com basename para QA)
        const qrUrl = `${baseUrl}${basename}/welcome/${envelope.qr_uuid}?token=${expToken}`;
        // 5. Gera o QR code normalmente
        const response = await fetch(buildApiUrl(`admin/qrcode?url=${encodeURIComponent(qrUrl)}`), {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Erro ao gerar QR code');
        const blob = await response.blob();
        loadCanvasQrCode(URL.createObjectURL(blob), qrUrl, participantDetails);
    };


    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>Participantes</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField label="SUBJID" value={newNickname} onChange={e => setNewNickname(e.target.value)} size="small" required />
                <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>Adicionar</Button>
            </Box>
            {isLoading ? <CircularProgress /> : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {/* <TableCell>ID</TableCell> */}
                                <TableCell>SUBJID</TableCell>
                                <TableCell>Envelopes</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {participants.map((p) => (
                                <TableRow key={p.id}>
                                    {/* <TableCell>{p.id}</TableCell> */}
                                    <TableCell>{p.nickname}</TableCell>
                                    <TableCell>{p.envelope_count || 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleViewDetails(p)} size="small" disabled={p.envelope_count === 0}><VisibilityIcon /></IconButton>
                                        {editId === p.id ? (
                                            <IconButton onClick={() => handleSave(p.id)} size="small"><Save /></IconButton>
                                        ) : (
                                            <IconButton onClick={() => handleEdit(p.id, p.nickname)} size="small" disabled={p.envelope_count > 0}><Edit /></IconButton>
                                        )}
                                        <IconButton onClick={() => handleDelete(p.id)} size="small" color="error" disabled={p.envelope_count > 0}><Delete /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {/* Modal de confirmação de exclusão */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja excluir o participante <b>{participantToDelete?.nickname}</b>? Esta ação não pode ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Excluir</Button>
                </DialogActions>
            </Dialog>
            {/* Modal de detalhes do participante */}
            <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Detalhes de {participantDetails?.nickname}</DialogTitle>
                <DialogContent>
                    {envelopes.length === 0 ? (
                        <Typography>Nenhum envelope encontrado.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID da Sentença</TableCell>
                                    <TableCell>Data de Criação</TableCell>
                                    <TableCell>Usuário Criação</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {envelopes.map((env) => (
                                    <TableRow key={env.envelope_id}>
                                        <TableCell>{env.envelope_id}</TableCell>
                                        <TableCell>{env.created_at ? new Date(env.created_at).toLocaleString() : '-'}</TableCell>
                                        <TableCell>{participantDetails?.nickname}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => env.qr_uuid && handleDownloadQr(env)} size="small"><QrCode2Icon /></IconButton>
                                            <IconButton onClick={() => handlePrintSentence(env)} size="small"><PrintIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDetailsDialog(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminParticipants;
